<template>
  <div class="sb-navigation">
    <div class="position-relative py-3">
      <div
        v-for="(nav, nav_idx) in tcfd_contents"
        :key="`navigation-group-${nav_idx}-${nav.category_code}`"
        class="navigation-group"
      >
        <div :class="['d-flex align-items-center mb-3 pl-3', { 'mt-4': nav_idx !== 0 }]">
          <h5
            class="nav-title nav-title--parent"
            :data-observe-anchor="'rp' + nav.category_code"
            :class="{
              'navigation-indicator': nav_idx === 0
            }"
          >
            <b-link
              class="nav-anchor text-keep-all text-decoration-none"
              @click="scroll_to_title('rp' + nav.category_code)"
            >
              <span v-show="lang === 'KOR'">{{ nav.category_name }}</span>
              <span v-show="lang === 'ENG'">{{ nav.category_name_e }}</span>
            </b-link>
          </h5>
        </div>

        <h6
          v-for="(subtopic, subtopic_idx) in nav.indicators"
          :key="'g' + subtopic_idx"
          :class="['nav-title nav-title--child', 'mb-0', 'ml-3']"
          :data-observe-anchor="'w' + subtopic.tcfd_code"
        >
          <b-link
            class="nav-anchor text-keep-all text-decoration-none"
            @click="scroll_to_title('w' + subtopic.tcfd_code)"
          >
            <span v-show="lang === 'KOR'">
              {{ subtopic.tcfd_code }}
            </span>
            <span v-show="lang === 'ENG'">{{ subtopic.tcfd_code }}</span>
          </b-link>
        </h6>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'TCFDTocViewer',
  computed: {
    ...mapGetters('story_board', ['selected_lv2']),
    ...mapState('report_contents', ['tcfd_contents']),
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report']),
    anchorList() {
      return this.tcfd_contents.reduce((_anchors, navEle) => {
        if (navEle?.category_code) {
          _anchors.push(`#rp${navEle.category_code}`);
        }
        if (navEle?.indicators && navEle.indicators.length) {
          navEle.indicators.forEach(subtopic => {
            _anchors.push(`#w${subtopic.tcfd_code}`);
          });
        }
        return _anchors;
      }, []);
    }
  },
  // TODO(#736 follow-up): GriTocViewer/SASBTocViewer/DCTocViewer/StoryBoardTocViewer와 동일 패턴 — mixin 추출 권장.
  mounted() {
    window.addEventListener('scroll', this.handleWindowScroll);
    this.setupAnchorObserver();
  },
  beforeDestroy() {
    if (this.anchorObserver) {
      this.anchorObserver.disconnect();
      this.anchorObserver = null;
    }
    if (this.observerTimerId) {
      clearTimeout(this.observerTimerId);
      this.observerTimerId = null;
    }
    window.removeEventListener('scroll', this.handleWindowScroll);
  },
  data() {
    return {
      activatedAnchor: null,
      anchorObserver: null,
      intersectingAnchors: new Set(),
      observerTimerId: null
    };
  },
  methods: {
    setupAnchorObserver() {
      if (!this.anchorList) return;
      if (this.observerTimerId) clearTimeout(this.observerTimerId);
      this.observerTimerId = setTimeout(() => {
        this.observerTimerId = null;
        if (this.anchorObserver) {
          this.anchorObserver.disconnect();
        }
        this.intersectingAnchors.clear();
        this.anchorObserver = new IntersectionObserver(this.handlerObserveAnchor, {
          rootMargin: '-105px 0px -60% 0px',
          threshold: 0
        });
        this.anchorList.forEach(_anchor => {
          const _anchorTitle = document.querySelector(_anchor);
          // 기존 TCFD-only 가드: childElementCount 검사 유지
          if (_anchorTitle && _anchorTitle.childElementCount > 0) {
            this.anchorObserver.observe(_anchorTitle);
          }
        });
        const initialNav = document.querySelector('.navigation-indicator');
        const color = this.selected_report && this.selected_report.main_color;
        if (initialNav && color) {
          initialNav.style.color = color;
          initialNav.style.borderLeft = `4px solid ${color}`;
        }
      }, 400);
    },
    setActiveIndicator(anchorId) {
      if (!anchorId || this.activatedAnchor === anchorId) return;
      const _activedNavs = document.querySelectorAll('.navigation-indicator');
      _activedNavs.forEach(_activedNav => {
        _activedNav.classList.remove('navigation-indicator');
        _activedNav.style.color = '';
        _activedNav.style.borderLeft = '';
      });
      const _navToAnchor = document.querySelector(`[data-observe-anchor="${anchorId}"]`);
      if (_navToAnchor) {
        this.activatedAnchor = anchorId;
        const color = this.selected_report && this.selected_report.main_color;
        if (color) {
          const node = _navToAnchor.childNodes[0];
          node.classList.add('navigation-indicator');
          node.style.color = color;
          node.style.borderLeft = `4px solid ${color}`;
        }
      }
    },
    handlerObserveAnchor(entries) {
      entries.forEach(entry => {
        const anchorId = entry.target.id.replace(/#/g, '');
        if (entry.isIntersecting) {
          this.intersectingAnchors.add(anchorId);
        } else {
          this.intersectingAnchors.delete(anchorId);
        }
      });
      if (this.intersectingAnchors.size === 0) return;
      for (const anchor of this.anchorList) {
        const id = anchor.replace('#', '');
        if (this.intersectingAnchors.has(id)) {
          this.setActiveIndicator(id);
          break;
        }
      }
    },
    handleWindowScroll() {
      if (!this.anchorList || this.anchorList.length === 0) return;
      const atTop = window.scrollY <= 5;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 5;
      if (atTop) {
        this.setActiveIndicator(this.anchorList[0].replace('#', ''));
      } else if (atBottom) {
        this.setActiveIndicator(this.anchorList[this.anchorList.length - 1].replace('#', ''));
      }
    },
    scroll_to_title(link) {
      const element = document.getElementById(link);
      if (element) {
        element.style.scrollMarginTop = '100px';
        element.scrollIntoView({ behavior: 'smooth' });
        this.setActiveIndicator(link);
      }
    }
  }
};
</script>

<style></style>
